"use client";

import Link from "next/link";
import { NavItem } from "../types";
import { isItemActive } from "../utils";
import { cn } from "@/lib";
import {
	NavigationMenu,
	NavigationMenuContent,
	NavigationMenuItem,
	NavigationMenuLink,
	NavigationMenuList,
	NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";

interface DesktopNavMenuProps {
	navItems: NavItem[];
	currentPath: string;
}

/** Enlace hoja dentro del panel (hijo sin nietos, o nieto). */
const PanelLink = ({
	item,
	currentPath,
}: {
	item: NavItem;
	currentPath: string;
}) => {
	const active = item.path === currentPath;
	return (
		<li>
			<NavigationMenuLink
				asChild
				className={cn(
					"flex flex-row items-center gap-2 rounded-sm px-3 py-2 text-sm font-body transition-colors",
					"hover:bg-muted focus:bg-muted hover:text-terracotta focus:text-terracotta",
					active ? "text-terracotta" : "text-ink",
					!item.active && "cursor-not-allowed pointer-events-none opacity-50"
				)}
			>
				<Link href={item.active ? item.path : "#"}>
					<span>{item.label}</span>
				</Link>
			</NavigationMenuLink>
		</li>
	);
};

/** Hijo con nietos: cabecera-enlace + sub-lista indentada en el mismo panel. */
const PanelSection = ({
	item,
	currentPath,
}: {
	item: NavItem;
	currentPath: string;
}) => {
	const active = isItemActive(item, currentPath);
	return (
		<li className="mt-1 first:mt-0">
			<NavigationMenuLink
				asChild
				className={cn(
					"flex flex-row items-center gap-2 rounded-sm px-3 py-2 text-sm font-body font-medium transition-colors",
					"hover:bg-muted focus:bg-muted hover:text-terracotta focus:text-terracotta",
					active ? "text-terracotta" : "text-ink",
					!item.active && "cursor-not-allowed pointer-events-none opacity-50"
				)}
			>
				<Link href={item.active ? item.path : "#"}>
					<span>{item.label}</span>
				</Link>
			</NavigationMenuLink>
			<ul className="ml-3 mt-0.5 flex flex-col gap-0.5 border-l border-border/60 pl-2">
				{item.children?.map((sub) => (
					<PanelLink key={sub.path} item={sub} currentPath={currentPath} />
				))}
			</ul>
		</li>
	);
};

export const DesktopNavMenu = ({
	navItems,
	currentPath,
}: DesktopNavMenuProps) => {
	return (
		<NavigationMenu viewport={false}>
			<NavigationMenuList className="gap-1">
				{navItems.map((item) => {
					if (!item.visible) return null;
					const active = isItemActive(item, currentPath);

					if (item.children?.length) {
						return (
							<NavigationMenuItem key={item.path}>
								<NavigationMenuTrigger
									className={cn(
										"relative bg-transparent px-4 py-2 text-sm font-body tracking-wide transition-colors",
										"hover:bg-transparent focus:bg-transparent data-[state=open]:bg-transparent data-[state=open]:hover:bg-transparent data-[state=open]:focus:bg-transparent",
										"after:pointer-events-none after:absolute after:bottom-1 after:left-4 after:right-4 after:h-[2px] after:rounded-full after:bg-terracotta after:opacity-0 after:transition-opacity after:duration-200",
										active
											? "text-terracotta after:opacity-100"
											: "text-ink hover:text-terracotta hover:after:opacity-60 data-[state=open]:text-terracotta data-[state=open]:after:opacity-60"
									)}
								>
									{item.label}
								</NavigationMenuTrigger>
								<NavigationMenuContent>
									<ul className="grid w-[240px] gap-0.5 p-1">
										{item.children.map((child) =>
											child.children?.length ? (
												<PanelSection
													key={child.path}
													item={child}
													currentPath={currentPath}
												/>
											) : (
												<PanelLink
													key={child.path}
													item={child}
													currentPath={currentPath}
												/>
											)
										)}
									</ul>
								</NavigationMenuContent>
							</NavigationMenuItem>
						);
					}

					return (
						<NavigationMenuItem key={item.path}>
							<NavigationMenuLink
								asChild
								className={cn(
									"relative inline-flex h-9 items-center px-4 py-2 text-sm font-body tracking-wide transition-colors",
									"hover:bg-transparent focus:bg-transparent",
									"after:pointer-events-none after:absolute after:bottom-1 after:left-4 after:right-4 after:h-[2px] after:rounded-full after:bg-terracotta after:opacity-0 after:transition-opacity after:duration-200",
									active
										? "text-terracotta after:opacity-100"
										: "text-ink hover:text-terracotta hover:after:opacity-60",
									!item.active &&
										"cursor-not-allowed pointer-events-none opacity-50"
								)}
							>
								<Link href={item.active ? item.path : "#"}>{item.label}</Link>
							</NavigationMenuLink>
						</NavigationMenuItem>
					);
				})}
			</NavigationMenuList>
		</NavigationMenu>
	);
};
